// Simple User Authentication - No credit card required
// Uses localStorage for now, can be upgraded to cloud auth later

import { logger } from '../utils/logger';

export interface User {
  id: string;
  email: string;
  name: string;
  createdAt: string;
  isGuest: boolean;
}

class UserAuthService {
  private currentUser: User | null = null;
  private lastStorageCheck: number = 0;
  private readonly STORAGE_CHECK_INTERVAL = 1000; // Only check storage every 1 second max

  constructor() {
    this.loadUserFromStorage();
  }

  // Create a new user account (free)
  async createUser(email: string, name: string): Promise<User> {
    const user: User = {
      id: this.generateUserId(),
      email,
      name,
      createdAt: new Date().toISOString(),
      isGuest: false
    };

    // Save to localStorage
    localStorage.setItem('currentUser', JSON.stringify(user));
    this.currentUser = user;
    
    logger.debug('User created:', user.name);
    return user;
  }

  // Sign in existing user
  async signIn(email: string): Promise<User | null> {
    const savedUser = localStorage.getItem('currentUser');
    if (savedUser) {
      const user = JSON.parse(savedUser);
      if (user.email === email) {
        this.currentUser = user;
        logger.debug('User signed in:', user.name);
        return user;
      }
    }
    return null;
  }

  // Create guest user (no email required)
  createGuestUser(): User {
    const guestUser: User = {
      id: 'guest_' + this.generateUserId(),
      email: '',
      name: 'Guest User',
      createdAt: new Date().toISOString(),
      isGuest: true
    };

    localStorage.setItem('currentUser', JSON.stringify(guestUser));
    this.currentUser = guestUser;
    
    logger.debug('Guest user created');
    return guestUser;
  }

  // Get current user (optimized - only reloads when needed)
  getCurrentUser(): User | null {
    // Only reload from storage if enough time has passed (performance optimization)
    // This prevents excessive localStorage reads while still keeping data fresh
    const now = Date.now();
    if (now - this.lastStorageCheck > this.STORAGE_CHECK_INTERVAL) {
      this.loadUserFromStorage();
      this.lastStorageCheck = now;
    }
    return this.currentUser;
  }
  
  // Force reload from storage (call this after auth state changes)
  refreshUser(): void {
    this.loadUserFromStorage();
    this.lastStorageCheck = Date.now();
  }

  // Check if user is logged in
  isLoggedIn(): boolean {
    return this.currentUser !== null;
  }

  /** No profile or explicit guest — not a registered (linked) account in local profile. */
  isPracticeGuestUser(): boolean {
    const u = this.getCurrentUser();
    return !u || u.isGuest;
  }

  /**
   * "Registered" in the product sense: user has completed an auth flow and we have
   * a persisted authenticated session marker (set by AuthCallback / login / checkoutAuth).
   * This is intentionally separate from `currentUser.isGuest`, because some flows may create
   * a local profile without a real auth session.
   */
  isAuthenticated(): boolean {
    try {
      return localStorage.getItem('userAuthenticated') === 'true';
    } catch {
      return false;
    }
  }

  // Sign out
  signOut(): void {
    this.currentUser = null;
    localStorage.removeItem('currentUser');
    logger.debug('User signed out');
  }

  // Load user from storage (public so it can be called after OAuth sign-up)
  loadUserFromStorage(): void {
    const saved = localStorage.getItem('currentUser');
    if (saved) {
      try {
        this.currentUser = JSON.parse(saved);
      } catch (error) {
        logger.error('Error loading user from storage:', error);
        this.currentUser = null;
      }
    } else {
      this.currentUser = null;
    }
  }

  // Generate unique user ID
  private generateUserId(): string {
    return 'user_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  // Update user profile
  updateProfile(name: string): void {
    if (this.currentUser) {
      this.currentUser.name = name;
      localStorage.setItem('currentUser', JSON.stringify(this.currentUser));
      logger.debug('Profile updated:', name);
    }
  }
}

export const userAuth = new UserAuthService();